import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { SearchX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useLocalizedPath } from "@/i18n/useLocalizedPath";

// Common scanner / probe paths we'll see in production traffic. Logging
// these as errors floods Sentry with bot noise that has no user signal.
const BOT_PATH_RE = /^\/(wp-admin|wp-login|wordpress|\.env|\.git|admin\.php|phpmyadmin|xmlrpc\.php|backup|cgi-bin|vendor|\.aws|\.ssh|\.well-known\/security)/i;

// A missing article is its own case: the reader wanted something to read, and
// the blog index is a far better answer than the homepage.
const BLOG_PATH_RE = /^\/(es\/)?blog(\/|$)/;

const NotFound = () => {
  const location = useLocation();
  const { t } = useTranslation("common");
  const lp = useLocalizedPath();
  const isBlog = BLOG_PATH_RE.test(location.pathname);

  useEffect(() => {
    if (BOT_PATH_RE.test(location.pathname)) return;
    // Downgrade error → warn: a 404 from a real user is worth knowing about,
    // but it's a UX/SEO issue, not a runtime error worth alerting on.
    console.warn("404:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background auth-bg-orbs">
      <div className="glass-card rounded-2xl p-12 text-center max-w-md mx-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex justify-center mb-6">
          <div className="bg-primary/10 rounded-2xl p-4">
            <SearchX className="h-12 w-12 text-primary" />
          </div>
        </div>
        <h1 className="text-7xl font-bold mb-4 bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">
          404
        </h1>
        <p className="text-xl font-semibold mb-2">
          {t(isBlog ? "notFound.articleTitle" : "notFound.title")}
        </p>
        <p className="text-lg text-muted-foreground mb-8">
          {t(isBlog ? "notFound.articleBody" : "notFound.body")}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {isBlog && (
            <Link to={lp("/blog")}>
              <Button className="btn-gradient text-white border-0 px-8 py-3 w-full sm:w-auto">
                {t("notFound.backToBlog")}
              </Button>
            </Link>
          )}
          <Link to={lp("/")}>
            <Button
              variant={isBlog ? "outline" : "default"}
              className={isBlog ? "px-8 py-3 w-full sm:w-auto" : "btn-gradient text-white border-0 px-8 py-3 w-full sm:w-auto"}
            >
              {t("notFound.backHome")}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
